import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Flex, Text, HStack, VStack, Spinner, Icon,
  Select, SimpleGrid, Button, IconButton, Tooltip, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Textarea,
} from '@chakra-ui/react';
import {
  FiLogIn, FiUser, FiUsers, FiAlertTriangle, FiDownload,
  FiRefreshCw, FiChevronLeft, FiChevronRight, FiAlertCircle,
  FiEdit3, FiFileText,
} from 'react-icons/fi';
import { getAccessLogs, listStudentStatuses, upsertStudentStatus } from 'services/qrService';
import { getCustomersWithOptions } from 'services/woo';
import { getSurveyResponse } from 'services/surveyService';
import { AVATAR_GRADIENTS, getInitials } from 'utils/avatarStyles';
import { buildNormalizedGroupOptions, getUserGroups } from 'utils/courseUtils';
import StudentHistoryModal, { JUSTIFY_TYPE_LABEL, findActiveStatus } from './StudentHistoryModal';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

// ─── Date utils ──────────────────────────────────────────────────────────────

// Día calendario en hora LOCAL (no UTC) — new Date().toISOString().slice(0,10)
// da el día UTC, que entre ~20:00 y 23:59 hora Chile ya es el día siguiente.
const localDateStr = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const todayStr = () => localDateStr();

const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const getMondayOf = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  const dow = d.getDay();
  return addDays(dateStr, dow === 0 ? -6 : 1 - dow);
};

const getMonthStartOf = (dateStr) => dateStr.slice(0, 8) + '01';

const getWeekDays = (mondayStr) =>
  Array.from({ length: 5 }, (_, i) => addDays(mondayStr, i));

const getMonthDays = (monthStart) => {
  const d = new Date(monthStart + 'T12:00:00');
  const count = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const day = new Date(d.getFullYear(), d.getMonth(), i + 1);
    return day.toISOString().slice(0, 10);
  });
};

const isWeekend = (dateStr) => {
  const dow = new Date(dateStr + 'T12:00:00').getDay();
  return dow === 0 || dow === 6;
};

const isFuture = (dateStr) => dateStr > todayStr();

const fmtTime = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    : null;

const fmtWeekRange = (mondayStr) => {
  const mon = new Date(mondayStr + 'T12:00:00');
  const fri = new Date(mondayStr + 'T12:00:00');
  fri.setDate(fri.getDate() + 4);
  return (
    mon.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) +
    ' – ' +
    fri.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })
  );
};

const fmtMonthYear = (monthStart) =>
  new Date(monthStart + 'T12:00:00').toLocaleDateString('es-CL', {
    month: 'long',
    year: 'numeric',
  });

const fmtDayHeader = (dateStr) => {
  const d = new Date(dateStr + 'T12:00:00');
  return {
    dow: d.toLocaleDateString('es-CL', { weekday: 'short' }).slice(0, 3),
    num: d.getDate(),
  };
};

const getMonthFirstDow = (monthStart) => {
  const dow = new Date(monthStart + 'T12:00:00').getDay();
  return dow === 0 ? 6 : dow - 1; // 0=Mon … 6=Sun
};

// ─── Cross-reference ─────────────────────────────────────────────────────────

const getStudentDayStatus = (rut, dayLogs) => {
  const sl = (dayLogs || [])
    .filter(l => l.rut === rut)
    .sort((a, b) => new Date(a.scanned_at) - new Date(b.scanned_at));
  const entryLog = sl.find(l => l.access_type === 'entry');
  const exitLogs = sl.filter(l => l.access_type === 'exit');
  const exitLog = exitLogs[exitLogs.length - 1];
  const earlyLog = exitLog && exitLog.early_exit ? exitLog : null;
  let status = 'absent';
  if (entryLog && earlyLog) status = 'early_exit';
  else if (exitLog) status = 'exited';
  else if (entryLog) status = 'present';
  return { status, entryLog, exitLog, earlyLog };
};

// ─── Status config ───────────────────────────────────────────────────────────

const SC = {
  present:    { label: 'Presente',       bg: '#DCF1E6', color: '#1E7A4E', letter: 'P' },
  exited:     { label: 'Salió',          bg: '#FCE7CC', color: '#B5631A', letter: 'S' },
  early_exit: { label: 'Sal. anticip.',  bg: '#FEF3C7', color: '#92400E', letter: 'E' },
  absent:     { label: 'Ausente',        bg: '#F3F4F6', color: '#9CA3AF', letter: 'A' },
};

// ─── Tardanza ───────────────────────────────────────────────────────────────

const isLateEntry = (entryLog, threshold) =>
  !!(entryLog && threshold && fmtTime(entryLog.scanned_at) > threshold);

// ─── Rate color ───────────────────────────────────────────────────────────────

const rateColor = (rate) => {
  if (rate >= 90) return { bg: '#BBDFC9', color: '#1E7A4E' };
  if (rate >= 75) return { bg: '#DCF1E6', color: '#2E8C5A' };
  if (rate >= 60) return { bg: '#FEF3C7', color: '#92400E' };
  if (rate >= 40) return { bg: '#FCE7CC', color: '#B5631A' };
  return { bg: '#FEE2E2', color: '#DC2626' };
};

// ─── Avatar ───────────────────────────────────────────────────────────────────

const rutGrad = (rut) => {
  let h = 0;
  for (let i = 0; i < (rut || '').length; i++) h = (h * 31 + rut.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
};

const Av = ({ name, rut, size = 36 }) => (
  <Box
    w={`${size}px`} h={`${size}px`} borderRadius="full" flexShrink={0}
    bgGradient={rutGrad(rut)}
    display="flex" alignItems="center" justifyContent="center"
    color="#fff" fontWeight="700" fontSize={`${Math.round(size * 0.33)}px`}
  >
    {getInitials(name)}
  </Box>
);

// ─── Fetch helpers ────────────────────────────────────────────────────────────

const fetchDayLogs = (date) =>
  getAccessLogs({ date, per_page: 500, page: 1 })
    .then(r => r.data ?? [])
    .catch(() => []);

const fetchBatchDates = async (dates) => {
  const BATCH = 5;
  const result = {};
  for (let i = 0; i < dates.length; i += BATCH) {
    const chunk = dates.slice(i, i + BATCH);
    const res = await Promise.all(chunk.map(d => fetchDayLogs(d).then(logs => [d, logs])));
    res.forEach(([d, logs]) => { result[d] = logs; });
  }
  return result;
};

// ─── MatrixCell ───────────────────────────────────────────────────────────────

const MatrixCell = ({ rut, date, logsCache, loadingDates, studentStatuses, lateThreshold }) => {
  const loading = loadingDates.has(date);
  const logs = logsCache[date];

  if (loading) {
    return (
      <Flex w="68px" h="46px" align="center" justify="center"
        borderRadius="8px" bg="cream.bg2">
        <Spinner size="xs" color="ink.400" />
      </Flex>
    );
  }

  if (!logs) {
    return (
      <Box w="68px" h="46px" borderRadius="8px"
        bg={isFuture(date) ? 'transparent' : 'cream.bg2'}
        border={isFuture(date) ? '1px dashed' : 'none'}
        borderColor="cream.line2"
      />
    );
  }

  const { status, entryLog } = getStudentDayStatus(rut, logs);
  const justification = status === 'absent' ? findActiveStatus(studentStatuses, rut, date) : null;
  const late = isLateEntry(entryLog, lateThreshold);
  let cfg;
  if (justification) {
    cfg = { label: `Justificado · ${JUSTIFY_TYPE_LABEL[justification.type] ?? ''}`, bg: '#E2ECFB', color: '#2A66B4', letter: 'J' };
  } else if (late && status !== 'absent') {
    cfg = { label: 'Llegó tarde', bg: '#FEF3C7', color: '#92400E', letter: 'T' };
  } else {
    cfg = SC[status];
  }
  const t = entryLog ? fmtTime(entryLog.scanned_at) : null;

  return (
    <Flex
      w="68px" h="46px" borderRadius="8px" bg={cfg.bg}
      direction="column" align="center" justify="center" gap="1px"
      title={`${cfg.label}${t ? ` · ${t}` : ''}`}
    >
      <Text fontSize="11px" fontWeight="800" color={cfg.color} lineHeight={1}>
        {cfg.letter}
      </Text>
      {t && (
        <Text fontSize="9.5px" fontWeight="600" color={cfg.color} opacity={0.85}>
          {t}
        </Text>
      )}
    </Flex>
  );
};

// ─── CalendarCell ─────────────────────────────────────────────────────────────

const getAttendanceRate = (students, dayLogs) => {
  if (!dayLogs || !students.length) return null;
  const present = students.filter(
    s => getStudentDayStatus(s.username, dayLogs).status !== 'absent'
  ).length;
  return Math.round((present / students.length) * 100);
};

const CalendarCell = ({ date, students, logsCache, loadingDates, isSelected, onSelect }) => {
  const weekend = isWeekend(date);
  const future = isFuture(date);
  const loading = !weekend && !future && loadingDates.has(date);
  const logs = logsCache[date];
  const isToday = date === todayStr();
  const dayNum = new Date(date + 'T12:00:00').getDate();
  const rate = weekend || future ? null : getAttendanceRate(students, logs);
  const rc = rate !== null ? rateColor(rate) : null;

  return (
    <Flex
      minH="52px" p="6px" borderRadius="10px" direction="column"
      bg={isSelected ? 'coral.tint' : rc ? rc.bg : weekend || future ? 'transparent' : 'cream.bg'}
      border="1.5px solid"
      borderColor={isSelected ? 'coral.500' : isToday ? 'coral.500' : weekend || future ? 'transparent' : 'cream.line'}
      cursor={!weekend && !future ? 'pointer' : 'default'}
      opacity={weekend ? 0.35 : future ? 0.4 : 1}
      _hover={!weekend && !future ? { borderColor: 'coral.500', opacity: 0.85 } : undefined}
      onClick={!weekend && !future ? onSelect : undefined}
      transition="all .15s"
    >
      <Text
        fontSize="12px" lineHeight={1}
        fontWeight={isToday ? '800' : '500'}
        color={isToday ? 'coral.600' : weekend ? 'ink.400' : 'ink.700'}
      >
        {dayNum}
      </Text>
      <Box flex={1} display="flex" alignItems="flex-end">
        {loading && <Spinner size="xs" color="ink.400" />}
        {!loading && rate !== null && (
          <Text fontSize="10px" fontWeight="700" color={rc.color}>{rate}%</Text>
        )}
      </Box>
    </Flex>
  );
};

// ─── DayView ──────────────────────────────────────────────────────────────────

const DayView = ({ students, logsCache, loadingDates, selectedDate, groupLabel, studentStatuses, onJustify, onViewHistory, onViewFicha, lateThreshold }) => {
  const loading = loadingDates.has(selectedDate);
  const dayLogs = logsCache[selectedDate] ?? [];

  const items = useMemo(() => students.map(student => {
    const name =
      [student.first_name, student.last_name].filter(Boolean).join(' ') || student.username;
    const { status, entryLog, exitLog, earlyLog } = getStudentDayStatus(student.username, dayLogs);
    const justification = status === 'absent'
      ? findActiveStatus(studentStatuses, student.username, selectedDate)
      : null;
    const late = isLateEntry(entryLog, lateThreshold);
    return { student, name, status, entryLog, exitLog, earlyLog, justification, late };
  }), [students, dayLogs, studentStatuses, selectedDate, lateThreshold]);

  const sorted = useMemo(() => {
    const order = { present: 0, exited: 1, early_exit: 2, absent: 3 };
    return [...items].sort((a, b) => (order[a.status] ?? 4) - (order[b.status] ?? 4));
  }, [items]);

  const stats = useMemo(() => ({
    total:   items.length,
    here:    items.filter(i => i.status !== 'absent').length,
    late:    items.filter(i => i.status !== 'absent' && i.late).length,
    early:   items.filter(i => i.status === 'early_exit').length,
    absent:  items.filter(i => i.status === 'absent').length,
  }), [items]);

  const pct = stats.total > 0 ? Math.round((stats.here / stats.total) * 100) : 0;

  return (
    <Box>
      {/* Attendance rate bar */}
      {stats.total > 0 && (
        <Box
          bg="white" border="1px solid" borderColor="cream.line"
          borderRadius="18px" p={4} mb={4}
          boxShadow="0 2px 8px -2px rgba(35,30,26,.06)"
        >
          <Flex justify="space-between" align="center" mb="10px">
            <Text fontSize="13px" fontWeight="700" color="ink.700">
              Asistencia — {groupLabel}
            </Text>
            <Text
              fontSize="26px" fontWeight="800" letterSpacing="-0.03em"
              color={pct >= 80 ? '#1E7A4E' : pct >= 60 ? '#D97706' : '#DC2626'}
            >
              {pct}%
            </Text>
          </Flex>
          <Box h="8px" bg="cream.bg2" borderRadius="full" overflow="hidden">
            <Box
              h="100%" borderRadius="full" w={`${pct}%`} transition="width .4s"
              bg={pct >= 80 ? '#3DB871' : pct >= 60 ? '#F59E0B' : '#EF4444'}
            />
          </Box>
          <HStack mt="10px" spacing="18px" flexWrap="wrap">
            {[
              { label: 'Asistieron', value: stats.here,   color: '#1E7A4E' },
              { label: 'Llegaron tarde', value: stats.late, color: '#92400E' },
              { label: 'Sal. anticipadas', value: stats.early, color: '#92400E' },
              { label: 'Ausentes',   value: stats.absent, color: '#9CA3AF' },
            ].map(({ label, value, color }) => (
              <Flex key={label} align="center" gap="5px">
                <Box w="7px" h="7px" borderRadius="full" bg={color} />
                <Text fontSize="12px" fontWeight="600" color="ink.400">{label}</Text>
                <Text fontSize="12px" fontWeight="800" color="ink.700">{value}</Text>
              </Flex>
            ))}
          </HStack>
        </Box>
      )}

      {/* Student list */}
      <Box
        bg="white" border="1px solid" borderColor="cream.line"
        borderRadius="22px" overflow="hidden"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
      >
        <Flex
          bg="cream.bg2" px={4} py="11px"
          borderBottom="1px solid" borderColor="cream.line"
          align="center" gap="10px"
        >
          <Text flex={1} fontSize="11px" fontWeight="800" color="ink.400"
            textTransform="uppercase" letterSpacing=".06em">
            Alumno
          </Text>
          <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase"
            letterSpacing=".06em" display={{ base: 'none', sm: 'block' }} minW="58px" textAlign="right">
            Entrada
          </Text>
          <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase"
            letterSpacing=".06em" display={{ base: 'none', md: 'block' }} minW="58px" textAlign="right">
            Salida
          </Text>
          <Text fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase"
            letterSpacing=".06em" minW="90px" textAlign="right">
            Estado
          </Text>
        </Flex>

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="coral.500" />
            <Text fontSize="13px" color="ink.400">Cargando…</Text>
          </VStack>
        ) : sorted.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Icon as={FiUsers} color="ink.400" boxSize={8} />
            <Text color="ink.400" fontSize="13px">Sin alumnos en este curso</Text>
          </VStack>
        ) : sorted.map((item, i) => {
          const cfg = item.justification
            ? { label: `Justificado · ${JUSTIFY_TYPE_LABEL[item.justification.type] ?? ''}`, bg: '#E2ECFB', color: '#2A66B4' }
            : item.late && item.status !== 'absent'
            ? { label: 'Llegó tarde', bg: '#FEF3C7', color: '#92400E' }
            : SC[item.status];
          return (
            <Flex
              key={item.student.id} align="center" gap="12px"
              px={4} py="12px"
              borderTop={i === 0 ? 'none' : '1px solid'} borderColor="cream.line"
              _hover={{ bg: 'coral.tint' }} transition="background .13s"
            >
              <Av name={item.name} rut={item.student.username} size={38} />
              <Box flex={1} minW={0}>
                <Text
                  fontWeight="700" fontSize="14px" color="ink.900" noOfLines={1}
                  cursor={onViewHistory ? 'pointer' : 'default'}
                  _hover={onViewHistory ? { color: 'coral.600', textDecoration: 'underline' } : undefined}
                  onClick={() => onViewHistory && onViewHistory(item.student)}
                >
                  {item.name}
                </Text>
                <Text fontSize="11.5px" color="ink.400" fontWeight="500" mt="1px">
                  {item.student.username}
                </Text>
              </Box>
              <Box textAlign="right" display={{ base: 'none', sm: 'block' }} minW="58px">
                <Text fontSize="13px" fontWeight="700" color={item.late ? '#92400E' : 'ink.900'}>
                  {item.entryLog ? fmtTime(item.entryLog.scanned_at) : '—'}
                </Text>
              </Box>
              <Box textAlign="right" display={{ base: 'none', md: 'block' }} minW="58px">
                <Text fontSize="13px" fontWeight="700" color="ink.900">
                  {item.exitLog ? fmtTime(item.exitLog.scanned_at) : '—'}
                </Text>
              </Box>
              <Flex
                px="9px" py="4px" borderRadius="999px"
                bg={cfg.bg} color={cfg.color}
                fontSize="11.5px" fontWeight="700" whiteSpace="nowrap"
                flexShrink={0} minW="90px" justify="center"
              >
                {cfg.label}
              </Flex>
              {item.earlyLog?.survey_response_id && onViewFicha && (
                <Tooltip label="Ver ficha">
                  <IconButton
                    icon={<FiFileText size={13} />} size="xs" w="26px" h="26px" minW="26px"
                    borderRadius="8px" bg="butter.bg" color="butter.ink" flexShrink={0}
                    _hover={{ bg: '#FCE7CC' }}
                    onClick={() => onViewFicha(item.earlyLog.survey_response_id)}
                    aria-label="Ver ficha"
                  />
                </Tooltip>
              )}
              {item.status === 'absent' && !item.justification && onJustify && (
                <Tooltip label="Justificar ausencia">
                  <IconButton
                    icon={<FiEdit3 size={13} />} size="xs" w="26px" h="26px" minW="26px"
                    borderRadius="8px" bg="cream.bg2" color="ink.400" flexShrink={0}
                    _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                    onClick={() => onJustify(item.student)}
                    aria-label="Justificar ausencia"
                  />
                </Tooltip>
              )}
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};

// ─── WeekMatrix ───────────────────────────────────────────────────────────────

const WEEK_LEGEND = [
  { letter: 'P', label: 'Presente',        bg: '#DCF1E6', color: '#1E7A4E' },
  { letter: 'T', label: 'Llegó tarde',     bg: '#FEF3C7', color: '#92400E' },
  { letter: 'S', label: 'Salió',           bg: '#FCE7CC', color: '#B5631A' },
  { letter: 'E', label: 'Salida anticip.', bg: '#FEF3C7', color: '#92400E' },
  { letter: 'J', label: 'Justificado',     bg: '#E2ECFB', color: '#2A66B4' },
  { letter: 'A', label: 'Ausente',         bg: '#F3F4F6', color: '#9CA3AF' },
];

const WeekMatrix = ({ students, logsCache, loadingDates, weekDays, onDayClick, studentStatuses, onViewHistory, lateThreshold }) => {
  const items = useMemo(() => students.map(student => {
    const name =
      [student.first_name, student.last_name].filter(Boolean).join(' ') || student.username;
    const absentCount = weekDays.filter(d => {
      const logs = logsCache[d];
      if (!logs) return false;
      return getStudentDayStatus(student.username, logs).status === 'absent';
    }).length;
    return { student, name, absentCount, isAlert: absentCount >= 2 };
  }), [students, logsCache, weekDays]);

  const dayStats = useMemo(() => weekDays.map(d => {
    if (isFuture(d)) return null;
    const logs = logsCache[d];
    if (logs === undefined || !students.length) return null;
    const present = students.filter(
      s => getStudentDayStatus(s.username, logs).status !== 'absent'
    ).length;
    return Math.round((present / students.length) * 100);
  }), [weekDays, logsCache, students]);

  if (!students.length) {
    return (
      <VStack py={14} spacing={2}>
        <Icon as={FiUsers} color="ink.400" boxSize={8} />
        <Text color="ink.400" fontSize="13px">Selecciona un curso para ver la matriz</Text>
      </VStack>
    );
  }

  return (
    <Box
      bg="white" border="1px solid" borderColor="cream.line"
      borderRadius="22px" overflow="hidden"
      boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
    >
      {/* Leyenda */}
      <HStack
        px={4} py="10px" spacing="14px" flexWrap="wrap"
        borderBottom="1px solid" borderColor="cream.line"
      >
        <Text fontSize="11px" color="ink.400" fontWeight="600">Leyenda:</Text>
        {WEEK_LEGEND.map(it => (
          <Flex key={it.letter} align="center" gap="5px">
            <Flex w="18px" h="18px" borderRadius="5px" bg={it.bg} color={it.color}
              align="center" justify="center" fontSize="10px" fontWeight="800">
              {it.letter}
            </Flex>
            <Text fontSize="11px" color="ink.400" fontWeight="600">{it.label}</Text>
          </Flex>
        ))}
      </HStack>

      <Box overflowX="auto">
        <Box minW="610px">
          {/* Header */}
          <Flex
            bg="cream.bg2" borderBottom="1px solid" borderColor="cream.line"
            px={4} py="10px" gap="8px" align="center"
          >
            <Box flex={1} minW="180px">
              <Text fontSize="11px" fontWeight="800" color="ink.400"
                textTransform="uppercase" letterSpacing=".06em">
                {items.length} Alumnos
              </Text>
            </Box>
            {weekDays.map(d => {
              const { dow, num } = fmtDayHeader(d);
              return (
                <Flex
                  key={d} w="68px" direction="column" align="center" gap="1px"
                  cursor="pointer" onClick={() => onDayClick && onDayClick(d)}
                  _hover={{ opacity: 0.7 }} transition="opacity .15s"
                  opacity={isFuture(d) ? 0.4 : 1}
                >
                  <Text fontSize="10px" fontWeight="700" color="ink.400"
                    textTransform="uppercase" letterSpacing=".04em">
                    {dow}
                  </Text>
                  <Text fontSize="16px" fontWeight="800"
                    color={d === todayStr() ? 'coral.500' : 'ink.700'}>
                    {num}
                  </Text>
                </Flex>
              );
            })}
          </Flex>

          {/* Rows */}
          {items.map((item, i) => (
            <Flex
              key={item.student.id}
              align="center" gap="8px" px={4} py="8px"
              borderTop={i === 0 ? 'none' : '1px solid'} borderColor="cream.line"
              _hover={{ bg: 'coral.tint' }} transition="background .13s"
              borderLeft="3px solid"
              borderLeftColor={item.isAlert ? '#F59E0B' : 'transparent'}
            >
              <Flex flex={1} minW="180px" align="center" gap="10px">
                <Av name={item.name} rut={item.student.username} size={32} />
                <Box minW={0}>
                  <Text
                    fontWeight="700" fontSize="13px" color="ink.900" noOfLines={1}
                    cursor={onViewHistory ? 'pointer' : 'default'}
                    _hover={onViewHistory ? { color: 'coral.600', textDecoration: 'underline' } : undefined}
                    onClick={() => onViewHistory && onViewHistory(item.student)}
                  >
                    {item.name}
                  </Text>
                  <HStack spacing="6px">
                    <Text fontSize="10.5px" color="ink.400">{item.student.username}</Text>
                    {item.isAlert && (
                      <Flex align="center" gap="3px">
                        <Icon as={FiAlertCircle} color="#F59E0B" boxSize="10px" />
                        <Text fontSize="10px" fontWeight="700" color="#D97706">
                          {item.absentCount} aus.
                        </Text>
                      </Flex>
                    )}
                  </HStack>
                </Box>
              </Flex>
              {weekDays.map(d => (
                <MatrixCell
                  key={d} rut={item.student.username} date={d}
                  logsCache={logsCache} loadingDates={loadingDates}
                  studentStatuses={studentStatuses} lateThreshold={lateThreshold}
                />
              ))}
            </Flex>
          ))}

          {/* Footer: % per day */}
          <Flex
            bg="cream.bg2" borderTop="2px solid" borderColor="cream.line"
            px={4} py="10px" gap="8px" align="center"
          >
            <Box flex={1} minW="180px">
              <Text fontSize="11px" fontWeight="800" color="ink.400"
                textTransform="uppercase" letterSpacing=".06em">
                % asistencia
              </Text>
            </Box>
            {dayStats.map((pct, i) => {
              const rc = pct !== null ? rateColor(pct) : null;
              return (
                <Flex key={weekDays[i]} w="68px" align="center" justify="center">
                  {loadingDates.has(weekDays[i]) ? (
                    <Spinner size="xs" color="ink.400" />
                  ) : pct !== null ? (
                    <Text fontSize="14px" fontWeight="800" color={rc.color}>{pct}%</Text>
                  ) : (
                    <Text fontSize="13px" color="ink.400">—</Text>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

// ─── MonthView ────────────────────────────────────────────────────────────────

const MonthView = ({ students, logsCache, loadingDates, monthStart, onDaySelect, onViewHistory }) => {
  const monthDays = useMemo(() => getMonthDays(monthStart), [monthStart]);
  const [selectedDay, setSelectedDay] = useState(null);
  const firstDow = getMonthFirstDow(monthStart);
  const calendarSlots = [...Array(firstDow).fill(null), ...monthDays];

  const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const studentStats = useMemo(() => {
    const workDays = monthDays.filter(d => !isWeekend(d));
    const daysWithData = workDays.filter(d => !isFuture(d) && logsCache[d] !== undefined);

    return students.map(s => {
      const name = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.username;
      let present = 0;
      let absent = 0;
      daysWithData.forEach(d => {
        const { status } = getStudentDayStatus(s.username, logsCache[d]);
        if (status !== 'absent') present++;
        else absent++;
      });
      const total = daysWithData.length;
      const pct = total > 0 ? Math.round((present / total) * 100) : null;
      return { student: s, name, present, absent, total, pct, isChronic: pct !== null && pct < 80 };
    }).sort((a, b) => (a.pct ?? 101) - (b.pct ?? 101));
  }, [students, logsCache, monthDays]);

  const chronicCount = studentStats.filter(s => s.isChronic).length;

  return (
    <VStack spacing={4} align="stretch">
      {/* Calendar heatmap */}
      <Box
        bg="white" border="1px solid" borderColor="cream.line"
        borderRadius="22px" p={4}
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
      >
        <Flex align="center" justify="space-between" mb={3}>
          <Text fontSize="13px" fontWeight="800" color="ink.700">
            Asistencia diaria — clic en un día para ver detalle
          </Text>
          {loadingDates.size > 0 && (
            <Flex align="center" gap="6px">
              <Spinner size="xs" color="ink.400" />
              <Text fontSize="11.5px" color="ink.400">cargando…</Text>
            </Flex>
          )}
        </Flex>

        <SimpleGrid columns={7} gap="4px" mb="4px">
          {DOW.map(d => (
            <Box key={d} textAlign="center" py="4px">
              <Text fontSize="10.5px" fontWeight="700"
                color={d === 'Sáb' || d === 'Dom' ? 'ink.400' : 'ink.700'}>
                {d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <SimpleGrid columns={7} gap="4px">
          {calendarSlots.map((date, i) =>
            date ? (
              <CalendarCell
                key={date} date={date} students={students}
                logsCache={logsCache} loadingDates={loadingDates}
                isSelected={date === selectedDay}
                onSelect={() => {
                  setSelectedDay(date);
                  onDaySelect && onDaySelect(date);
                }}
              />
            ) : (
              <Box key={`e-${i}`} minH="52px" />
            )
          )}
        </SimpleGrid>

        {/* Legend */}
        <HStack mt={3} spacing="14px" flexWrap="wrap">
          <Text fontSize="11px" color="ink.400" fontWeight="600">Asistencia:</Text>
          {[
            { label: '≥90%', rate: 95 },
            { label: '75–89%', rate: 80 },
            { label: '60–74%', rate: 65 },
            { label: '<60%', rate: 40 },
          ].map(({ label, rate }) => {
            const rc = rateColor(rate);
            return (
              <Flex key={label} align="center" gap="5px">
                <Box w="12px" h="12px" borderRadius="3px" bg={rc.bg} />
                <Text fontSize="11px" fontWeight="600" color="ink.400">{label}</Text>
              </Flex>
            );
          })}
        </HStack>
      </Box>

      {/* Student ranking */}
      {students.length > 0 && (
        <Box
          bg="white" border="1px solid" borderColor="cream.line"
          borderRadius="22px" overflow="hidden"
          boxShadow="0 2px 12px -4px rgba(35,30,26,.08)"
        >
          <Flex
            bg="cream.bg2" px={4} py="11px"
            borderBottom="1px solid" borderColor="cream.line"
            align="center" gap="10px"
          >
            <Text flex={1} fontSize="11px" fontWeight="800" color="ink.400"
              textTransform="uppercase" letterSpacing=".06em">
              Ranking de asistencia mensual
            </Text>
            {chronicCount > 0 && (
              <Flex align="center" gap="6px" bg="#FEF3C7" px={3} py="4px" borderRadius="999px">
                <Icon as={FiAlertCircle} color="#D97706" boxSize="13px" />
                <Text fontSize="11.5px" fontWeight="700" color="#92400E">
                  {chronicCount} con &lt;80%
                </Text>
              </Flex>
            )}
          </Flex>

          {studentStats.map((item, i) => (
            <Flex
              key={item.student.id} align="center" gap="12px"
              px={4} py="11px"
              borderTop={i === 0 ? 'none' : '1px solid'} borderColor="cream.line"
              _hover={{ bg: 'coral.tint' }} transition="background .13s"
              borderLeft="3px solid"
              borderLeftColor={item.isChronic ? '#F59E0B' : 'transparent'}
            >
              <Av name={item.name} rut={item.student.username} size={36} />
              <Box flex={1} minW={0}>
                <Flex align="center" gap="7px">
                  <Text
                    fontWeight="700" fontSize="13.5px" color="ink.900" noOfLines={1}
                    cursor={onViewHistory ? 'pointer' : 'default'}
                    _hover={onViewHistory ? { color: 'coral.600', textDecoration: 'underline' } : undefined}
                    onClick={() => onViewHistory && onViewHistory(item.student)}
                  >
                    {item.name}
                  </Text>
                  {item.isChronic && (
                    <Flex align="center" gap="3px" flexShrink={0}>
                      <Icon as={FiAlertTriangle} color="#F59E0B" boxSize="12px" />
                      <Text fontSize="10.5px" fontWeight="700" color="#D97706">
                        Inasistencia alta
                      </Text>
                    </Flex>
                  )}
                </Flex>
                <Text fontSize="11px" color="ink.400">{item.student.username}</Text>
              </Box>

              <Box w="130px" display={{ base: 'none', sm: 'block' }}>
                {item.pct !== null ? (
                  <>
                    <Box h="6px" bg="cream.bg2" borderRadius="full" overflow="hidden">
                      <Box
                        h="100%" borderRadius="full" w={`${item.pct}%`}
                        bg={item.pct >= 80 ? '#3DB871' : item.pct >= 60 ? '#F59E0B' : '#EF4444'}
                      />
                    </Box>
                    <Text fontSize="10.5px" fontWeight="600" color="ink.400" mt="3px">
                      {item.absent} aus. de {item.total} días
                    </Text>
                  </>
                ) : (
                  <Text fontSize="11px" color="ink.400">Sin datos aún</Text>
                )}
              </Box>

              <Box textAlign="right" minW="44px">
                {item.pct !== null ? (
                  <Text
                    fontSize="17px" fontWeight="800" letterSpacing="-0.02em"
                    color={item.pct >= 80 ? '#1E7A4E' : item.pct >= 60 ? '#D97706' : '#DC2626'}
                  >
                    {item.pct}%
                  </Text>
                ) : (
                  <Text fontSize="13px" color="ink.400">—</Text>
                )}
              </Box>
            </Flex>
          ))}
        </Box>
      )}
    </VStack>
  );
};

// ─── JustifyModal ───────────────────────────────────────────────────────────

const JustifyModal = ({ isOpen, onClose, student, date, onSaved }) => {
  const [type, setType] = useState('medical_leave');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) { setType('medical_leave'); setReason(''); }
  }, [isOpen, student]);

  if (!student) return null;
  const name = [student.first_name, student.last_name].filter(Boolean).join(' ') || student.username;

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertStudentStatus({
        id: null,
        wp_user_id: student.id,
        rut: student.username,
        type,
        reason: reason.trim() || null,
        notes: null,
        starts_at: `${date}T00:00:00`,
        ends_at: `${date}T23:59:59`,
        authorized_by: null,
        active: true,
      });
      toast({ title: 'Ausencia justificada', status: 'success', duration: 2500 });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({
        title: 'Error al guardar',
        description: e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message,
        status: 'error', duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
        <ModalHeader fontSize="16px" fontWeight="800" color="ink.900">Justificar ausencia</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="ink.400" mb={4}>
            {name} · {new Date(date + 'T12:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'long' })}
          </Text>
          <VStack spacing={3} align="stretch">
            <Box>
              <Text fontSize="12.5px" fontWeight="700" color="ink.700" mb="6px">Motivo</Text>
              <Select size="sm" borderRadius="9px" value={type} onChange={e => setType(e.target.value)}>
                {Object.entries(JUSTIFY_TYPE_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </Select>
            </Box>
            <Box>
              <Text fontSize="12.5px" fontWeight="700" color="ink.700" mb="6px">Detalle (opcional)</Text>
              <Textarea size="sm" borderRadius="9px" rows={2}
                placeholder="Ej: Certificado médico adjunto en secretaría"
                value={reason} onChange={e => setReason(e.target.value)} />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" borderRadius="9px" bg="coral.500" color="#fff"
            _hover={{ bg: 'coral.600' }} onClick={handleSave} isLoading={saving}>
            Guardar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// ─── Main AttendanceView ──────────────────────────────────────────────────────

const AttendanceView = () => {
  const [viewMode, setViewMode] = useState('day');
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [weekStart, setWeekStart] = useState(() => getMondayOf(todayStr()));
  const [monthStart, setMonthStart] = useState(() => getMonthStartOf(todayStr()));
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [logsCache, setLogsCache] = useState({});
  const [loadingDates, setLoadingDates] = useState(new Set());
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentStatuses, setStudentStatuses] = useState([]);
  const [justifyTarget, setJustifyTarget] = useState(null); // student a justificar
  const [historyStudent, setHistoryStudent] = useState(null); // student a ver historial
  const [isFichaOpen, setIsFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);

  // "Ver ficha" desde la fila del día (DayView) — mismo patrón ya replicado
  // en AccessLogs/index.js, StudentHistoryModal.js y los tabs de salidas.
  const handleOpenFicha = (responseId) => {
    setFichaData(null);
    setIsFichaOpen(true);
    getSurveyResponse(responseId)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  };
  const [lateThreshold, setLateThreshold] = useState(
    () => localStorage.getItem('attendance-late-threshold') || '08:30'
  );
  const cacheRef = useRef({});
  const inflightRef = useRef(new Set());
  const toast = useToast();

  // Load student statuses (licencias/suspensiones/salidas) — para detectar ausencias justificadas
  const loadStatuses = useCallback(() => {
    listStudentStatuses().then(res => setStudentStatuses(res.data ?? [])).catch(() => {});
  }, []);
  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  // Load groups
  useEffect(() => {
    const load = async () => {
      try {
        const rawGroups = await getUserGroups();
        const normalized = buildNormalizedGroupOptions(rawGroups);
        setGroups(normalized);
        if (normalized.length > 0) setSelectedGroup(normalized[0].code);
      } catch { /* no groups */ }
    };
    load();
  }, []);

  // Load students for selected group — vía endpoint propio /datofy/v1/users
  // (filtra group_name en el servidor, no se pagina todo el padrón del colegio)
  useEffect(() => {
    if (!selectedGroup) { setStudents([]); return; }
    let cancelled = false;
    const load = async () => {
      setStudentsLoading(true);
      try {
        const groupOption = groups.find(g => g.code === selectedGroup);
        const groupFilter = groupOption?.rawValues?.length ? groupOption.rawValues : selectedGroup;
        let all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const res = await getCustomersWithOptions({
            group_name: groupFilter, role: 'all', per_page: 100, page,
          });
          all = [...all, ...(res.data ?? [])];
          totalPages = res.totalPages || 1;
          page++;
        } while (page <= totalPages);
        all.sort((a, b) => {
          const na = `${a.last_name} ${a.first_name}`.toLowerCase();
          const nb = `${b.last_name} ${b.first_name}`.toLowerCase();
          return na.localeCompare(nb);
        });
        if (!cancelled) setStudents(all);
      } catch {
        if (!cancelled) setStudents([]);
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedGroup, groups]);

  // Fetch logs for dates not yet in cache
  const ensureDatesLoaded = useCallback(async (dates) => {
    const toFetch = dates.filter(
      d => !cacheRef.current[d] && !inflightRef.current.has(d)
    );
    if (!toFetch.length) return;
    toFetch.forEach(d => inflightRef.current.add(d));
    setLoadingDates(prev => new Set([...prev, ...toFetch]));
    try {
      const fetched = await fetchBatchDates(toFetch);
      Object.assign(cacheRef.current, fetched);
      setLogsCache(prev => ({ ...prev, ...fetched }));
    } catch {
      const fallback = {};
      toFetch.forEach(d => { fallback[d] = []; cacheRef.current[d] = []; });
      setLogsCache(prev => ({ ...prev, ...fallback }));
    } finally {
      toFetch.forEach(d => inflightRef.current.delete(d));
      setLoadingDates(prev => {
        const next = new Set(prev);
        toFetch.forEach(d => next.delete(d));
        return next;
      });
    }
  }, []);

  // Trigger load when view or period changes
  useEffect(() => {
    let dates;
    if (viewMode === 'day') dates = [selectedDate];
    else if (viewMode === 'week') dates = getWeekDays(weekStart);
    else dates = getMonthDays(monthStart).filter(d => !isWeekend(d));
    ensureDatesLoaded(dates);
  }, [viewMode, selectedDate, weekStart, monthStart, ensureDatesLoaded]);

  // Stats for day-mode tiles
  const dayStats = useMemo(() => {
    const logs = logsCache[selectedDate] ?? [];
    const ss = students.map(s => getStudentDayStatus(s.username, logs));
    return {
      total:  ss.length,
      here:   ss.filter(i => i.status !== 'absent').length,
      early:  ss.filter(i => i.status === 'early_exit').length,
      absent: ss.filter(i => i.status === 'absent').length,
    };
  }, [students, logsCache, selectedDate]);

  // Navigation
  const navPrev = () => {
    if (viewMode === 'day') setSelectedDate(d => addDays(d, -1));
    else if (viewMode === 'week') setWeekStart(d => addDays(d, -7));
    else setMonthStart(d => {
      const nd = new Date(d + 'T12:00:00');
      nd.setMonth(nd.getMonth() - 1);
      return nd.toISOString().slice(0, 8) + '01';
    });
  };
  const navNext = () => {
    if (viewMode === 'day') setSelectedDate(d => addDays(d, 1));
    else if (viewMode === 'week') setWeekStart(d => addDays(d, 7));
    else setMonthStart(d => {
      const nd = new Date(d + 'T12:00:00');
      nd.setMonth(nd.getMonth() + 1);
      return nd.toISOString().slice(0, 8) + '01';
    });
  };
  const navToday = () => {
    const t = todayStr();
    setSelectedDate(t);
    setWeekStart(getMondayOf(t));
    setMonthStart(getMonthStartOf(t));
  };

  const handleLateThresholdChange = (value) => {
    setLateThreshold(value);
    localStorage.setItem('attendance-late-threshold', value);
  };

  const periodLabel = viewMode === 'day'
    ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('es-CL', {
        weekday: 'long', day: 'numeric', month: 'long',
      })
    : viewMode === 'week'
    ? fmtWeekRange(weekStart)
    : fmtMonthYear(monthStart);

  const groupLabel = groups.find(g => g.code === selectedGroup)?.label ?? '';

  // Refresh current view
  const handleRefresh = () => {
    const dates =
      viewMode === 'day' ? [selectedDate]
      : viewMode === 'week' ? getWeekDays(weekStart)
      : getMonthDays(monthStart).filter(d => !isWeekend(d));
    dates.forEach(d => {
      delete cacheRef.current[d];
      inflightRef.current.delete(d);
    });
    setLogsCache(prev => {
      const n = { ...prev };
      dates.forEach(d => delete n[d]);
      return n;
    });
    ensureDatesLoaded(dates);
  };

  // Export CSV (day mode)
  const handleExport = () => {
    const logs = logsCache[selectedDate] ?? [];
    const rows = students.map(s => {
      const name = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.username;
      const { status, entryLog, exitLog, earlyLog } = getStudentDayStatus(s.username, logs);
      return [
        s.last_name, s.first_name, s.username,
        SC[status]?.label ?? status,
        entryLog ? fmtTime(entryLog.scanned_at) : '',
        exitLog ? fmtTime(exitLog.scanned_at) : '',
        earlyLog?.exit_reason ?? '',
      ];
    });
    if (!rows.length) return;
    const headers = ['Apellidos', 'Nombres', 'RUT', 'Estado', 'Entrada', 'Salida', 'Motivo'];
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `asistencia-${groupLabel}-${selectedDate}.csv`;
    link.click();
    toast({ title: 'CSV descargado', status: 'success', duration: 2000 });
  };

  return (
    <Box fontFamily="jakarta">

      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <Box
        bg="white" border="1px solid" borderColor="cream.line"
        borderRadius="18px" p="12px 16px" mb={4}
        boxShadow="0 2px 8px -2px rgba(35,30,26,.06)"
      >
        <Flex gap="10px" align="center" flexWrap="wrap">
          {/* Course */}
          <Select
            value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}
            h="40px" borderRadius="11px"
            border="1.5px solid" borderColor="cream.line2"
            bg="cream.bg" fontSize="13.5px" fontWeight="600" color="ink.700"
            w={{ base: 'full', sm: '220px' }}
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}
          >
            {groups.length === 0 && <option value="">Sin cursos</option>}
            {groups.map(g => <option key={g.code} value={g.code}>{g.label}</option>)}
          </Select>

          {/* View mode toggle */}
          <Flex bg="cream.bg2" borderRadius="12px" p="3px" gap="2px" flexShrink={0}>
            {[{ id: 'day', label: 'Día' }, { id: 'week', label: 'Semana' }, { id: 'month', label: 'Mes' }].map(mode => (
              <Button
                key={mode.id} h="34px" px={4} borderRadius="9px"
                bg={viewMode === mode.id ? 'white' : 'transparent'}
                color={viewMode === mode.id ? 'coral.600' : 'ink.400'}
                fontWeight="700" fontSize="13px"
                boxShadow={viewMode === mode.id ? '0 1px 4px -1px rgba(35,30,26,.14)' : 'none'}
                _hover={{ color: 'ink.900' }}
                onClick={() => setViewMode(mode.id)}
              >
                {mode.label}
              </Button>
            ))}
          </Flex>

          {/* Period navigation */}
          <Flex
            align="center" gap="4px"
            flex={{ base: '1 1 100%', md: 1 }}
            justify="center"
            minW={{ base: 0, md: '240px' }}
          >
            <IconButton
              icon={<FiChevronLeft />}
              h={{ base: '32px', md: '36px' }} w={{ base: '32px', md: '36px' }} flexShrink={0}
              borderRadius="9px"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={navPrev} aria-label="Anterior"
            />
            <Box px={{ base: 1, md: 3 }} textAlign="center" flex={{ base: 1, md: 'initial' }} minW={0}>
              <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}
                textTransform="capitalize">
                {periodLabel}
              </Text>
            </Box>
            <IconButton
              icon={<FiChevronRight />}
              h={{ base: '32px', md: '36px' }} w={{ base: '32px', md: '36px' }} flexShrink={0}
              borderRadius="9px"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              onClick={navNext} aria-label="Siguiente"
            />
            <Button
              h={{ base: '32px', md: '36px' }} px={{ base: '8px', md: '12px' }} flexShrink={0}
              borderRadius="9px" fontSize="12.5px" fontWeight="700"
              border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.400"
              _hover={{ borderColor: 'coral.500', color: 'coral.600' }}
              onClick={navToday}
            >
              Hoy
            </Button>
          </Flex>

          {/* Actions */}
          <HStack spacing="8px" flexShrink={0}>
            <Tooltip label="Hora límite — entradas después de esta hora se marcan como tardanza">
              <HStack spacing="6px" px="10px" h="40px" borderRadius="11px"
                border="1.5px solid" borderColor="cream.line2" bg="white">
                <Text fontSize="12px" color="ink.400" fontWeight="600" whiteSpace="nowrap">Tardanza</Text>
                <input
                  type="time" value={lateThreshold}
                  onChange={e => handleLateThresholdChange(e.target.value)}
                  style={{ border: 'none', outline: 'none', fontSize: '13px', fontWeight: 700, color: '#2B2723', background: 'transparent', width: '76px' }}
                />
              </HStack>
            </Tooltip>
            <Tooltip label="Actualizar datos">
              <IconButton
                icon={<FiRefreshCw />} h="40px" w="40px" borderRadius="11px"
                border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
                _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                isLoading={studentsLoading || loadingDates.size > 0}
                onClick={handleRefresh} aria-label="Actualizar"
              />
            </Tooltip>
            {viewMode === 'day' && (
              <Button
                h="40px" px={4} borderRadius="11px"
                border="1.5px solid" borderColor="cream.line2"
                bg="white" color="ink.700" fontWeight="700" fontSize="13.5px"
                _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
                leftIcon={<FiDownload size={14} />}
                onClick={handleExport} isDisabled={!students.length}
              >
                <Text display={{ base: 'none', sm: 'block' }}>CSV</Text>
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* ── Stats tiles (day only) ─────────────────────────────────────────── */}
      {viewMode === 'day' && (
        <SimpleGrid columns={{ base: 2, sm: 4 }} gap="12px" mb={4}>
          {[
            { label: 'Total padrón', value: dayStats.total,  bg: 'cream.bg2', color: 'ink.900',  icon: FiUsers },
            { label: 'Asistieron',   value: dayStats.here,   bg: '#DCF1E6',   color: '#1E7A4E',  icon: FiLogIn },
            { label: 'S. anticip.',  value: dayStats.early,  bg: '#FEF3C7',   color: '#92400E',  icon: FiAlertTriangle },
            { label: 'Ausentes',     value: dayStats.absent, bg: '#F3F4F6',   color: '#6B7280',  icon: FiUser },
          ].map(({ label, value, bg, color, icon }) => (
            <Flex key={label} bg="white" border="1px solid" borderColor="cream.line"
              borderRadius="16px" p={{ base: 3, md: 4 }} gap={{ base: '8px', md: '12px' }} align="center"
              boxShadow="0 2px 8px -2px rgba(35,30,26,.06)">
              <Flex w={{ base: '32px', md: '40px' }} h={{ base: '32px', md: '40px' }} borderRadius="12px" bg={bg}
                align="center" justify="center" flexShrink={0}>
                <Icon as={icon} color={color} boxSize={{ base: '15px', md: '19px' }} />
              </Flex>
              <Box minW={0}>
                <Text fontSize={{ base: '18px', md: '24px' }} fontWeight="800" color="ink.900" lineHeight={1}
                  letterSpacing="-0.03em" noOfLines={1}>{value}</Text>
                <Text fontSize={{ base: '10.5px', md: '12px' }} color="ink.400" fontWeight="600" mt="3px" noOfLines={1}>{label}</Text>
              </Box>
            </Flex>
          ))}
        </SimpleGrid>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {studentsLoading ? (
        <VStack py={14} spacing={3}>
          <Spinner size="lg" color="coral.500" />
          <Text fontSize="13px" color="ink.400">Cargando padrón del curso…</Text>
        </VStack>
      ) : !selectedGroup ? (
        <VStack py={14} spacing={2}>
          <Icon as={FiUsers} color="ink.400" boxSize={8} />
          <Text color="ink.400" fontSize="13px">Selecciona un curso para ver la asistencia</Text>
        </VStack>
      ) : (
        <>
          {viewMode === 'day' && (
            <DayView
              students={students} logsCache={logsCache} loadingDates={loadingDates}
              selectedDate={selectedDate} groupLabel={groupLabel}
              studentStatuses={studentStatuses} lateThreshold={lateThreshold}
              onJustify={(student) => setJustifyTarget(student)}
              onViewHistory={(student) => setHistoryStudent(student)}
              onViewFicha={handleOpenFicha}
            />
          )}
          {viewMode === 'week' && (
            <WeekMatrix
              students={students} logsCache={logsCache} loadingDates={loadingDates}
              weekDays={getWeekDays(weekStart)}
              onDayClick={date => { setSelectedDate(date); setViewMode('day'); }}
              studentStatuses={studentStatuses} lateThreshold={lateThreshold}
              onViewHistory={(student) => setHistoryStudent(student)}
            />
          )}
          {viewMode === 'month' && (
            <MonthView
              students={students} logsCache={logsCache} loadingDates={loadingDates}
              monthStart={monthStart}
              onDaySelect={date => { setSelectedDate(date); setViewMode('day'); }}
              onViewHistory={(student) => setHistoryStudent(student)}
            />
          )}
        </>
      )}

      <JustifyModal
        isOpen={!!justifyTarget}
        student={justifyTarget}
        date={selectedDate}
        onClose={() => setJustifyTarget(null)}
        onSaved={loadStatuses}
      />
      <StudentHistoryModal
        isOpen={!!historyStudent}
        student={historyStudent}
        onClose={() => setHistoryStudent(null)}
      />

      <ModalSurveyResponse
        isOpen={isFichaOpen}
        onClose={() => setIsFichaOpen(false)}
        customer={{ first_name: fichaData?.survey?.name ?? '', last_name: '', username: '' }}
        data={fichaData}
      />
    </Box>
  );
};

export default AttendanceView;
